import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Mail, Phone, RefreshCw, Lock, TrendingUp, BarChart3, Eye } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badge = (status) => {
  const map = {
    new: "bg-teal-100 text-teal-700 border-teal-200",
    read: "bg-blue-100 text-blue-700 border-blue-200",
    replied: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };
  return `text-xs font-semibold px-2.5 py-1 rounded-full border ${map[status] || map.new}`;
};

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(localStorage.getItem("astoria_admin_token") || "");
  const [inputToken, setInputToken] = useState("");
  const [inquiries, setInquiries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const params = { admin_token: t };
      if (filter !== "all") params.status = filter;
      const [{ data: inq }, { data: an }] = await Promise.all([
        axios.get(`${API}/contact`, { params }),
        axios.get(`${API}/analytics`, { params: { admin_token: t, days: 14 } }),
      ]);
      setInquiries(inq);
      setAnalytics(an);
    } catch (err) {
      toast({ title: "Access denied", description: "Invalid admin token." });
      setToken("");
      localStorage.removeItem("astoria_admin_token");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter]);

  const login = (e) => {
    e.preventDefault();
    if (!inputToken.trim()) return;
    localStorage.setItem("astoria_admin_token", inputToken.trim());
    setToken(inputToken.trim());
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status }, { params: { admin_token: token } });
      load();
    } catch {
      toast({ title: "Update failed" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, { params: { admin_token: token } });
      setInquiries((prev) => prev.filter((x) => x.id !== id));
    } catch {
      toast({ title: "Delete failed" });
    }
  };

  if (!token) {
    return (
      <section className="min-h-[70vh] bg-neutral-50 flex items-center justify-center py-16">
        <form onSubmit={login} className="bg-white rounded-2xl p-8 border border-neutral-100 shadow-sm w-full max-w-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-neutral-900">Admin Access</h1>
              <p className="text-xs text-neutral-500">Enter your admin token to continue</p>
            </div>
          </div>
          <input
            type="password"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder="Admin token"
            className="mt-6 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400"
          />
          <button
            type="submit"
            className="mt-4 w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-full transition-colors"
          >
            Enter Dashboard
          </button>
        </form>
      </section>
    );
  }

  const counts = inquiries.reduce(
    (acc, i) => {
      acc.total++;
      acc[i.status] = (acc[i.status] || 0) + 1;
      return acc;
    },
    { total: 0, new: 0, read: 0, replied: 0 }
  );

  return (
    <section className="bg-neutral-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-neutral-900">Inquiries Dashboard</h1>
            <p className="text-sm text-neutral-500">Manage messages sent through the contact form.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              className="inline-flex items-center gap-2 border border-neutral-200 bg-white text-neutral-700 hover:text-teal-600 hover:border-teal-400 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("astoria_admin_token");
                setToken("");
              }}
              className="text-sm text-neutral-500 hover:text-neutral-800"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "total", label: "Total", value: inquiries.length },
            { k: "new", label: "New", value: counts.new },
            { k: "read", label: "Read", value: counts.read },
            { k: "replied", label: "Replied", value: counts.replied },
          ].map((c) => (
            <div key={c.k} className="bg-white rounded-xl border border-neutral-100 p-4">
              <div className="text-xs uppercase tracking-wider text-neutral-500">{c.label}</div>
              <div className="mt-1 text-2xl font-extrabold text-neutral-900">{c.value}</div>
            </div>
          ))}
        </div>

        {/* ANALYTICS PANELS */}
        {analytics && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-neutral-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-teal-600 tracking-widest flex items-center gap-1">
                    <TrendingUp className="w-3.5 h-3.5" /> INQUIRIES · LAST {analytics.days} DAYS
                  </div>
                  <div className="mt-1 text-2xl font-extrabold text-neutral-900">
                    {analytics.trend.reduce((a, b) => a + b.count, 0)} <span className="text-sm text-neutral-500 font-medium">total</span>
                  </div>
                </div>
              </div>
              {(() => {
                const trend = analytics.trend;
                const max = Math.max(1, ...trend.map((t) => t.count));
                return (
                  <div className="mt-6">
                    <div className="flex items-end gap-2 h-40">
                      {trend.map((t) => {
                        const h = Math.max(2, Math.round((t.count / max) * 100));
                        return (
                          <div key={t.date} className="flex-1 flex flex-col items-center gap-1 group">
                            <div className="text-[10px] font-semibold text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity">
                              {t.count}
                            </div>
                            <div
                              className="w-full rounded-t-md bg-gradient-to-t from-teal-600 to-teal-400 hover:from-teal-700 hover:to-teal-500 transition-colors"
                              style={{ height: `${h}%` }}
                              title={`${t.date}: ${t.count}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 flex justify-between text-[10px] text-neutral-400">
                      <span>{trend[0]?.date}</span>
                      <span>{trend[trend.length - 1]?.date}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="bg-white rounded-2xl border border-neutral-100 p-6">
              <div className="text-xs font-bold text-teal-600 tracking-widest flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5" /> BY STATUS
              </div>
              <div className="mt-4 space-y-3">
                {Object.entries(analytics.by_status).map(([k, v]) => {
                  const total = Object.values(analytics.by_status).reduce((a, b) => a + b, 0) || 1;
                  const pct = Math.round((v / total) * 100);
                  const bar =
                    k === "new"
                      ? "bg-amber-500"
                      : k === "read"
                      ? "bg-blue-500"
                      : "bg-emerald-500";
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs font-semibold text-neutral-700 capitalize">
                        <span>{k}</span>
                        <span>{v} · {pct}%</span>
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                        <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-3 bg-white rounded-2xl border border-neutral-100 p-6">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-teal-600 tracking-widest flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5" /> TOP PAGES · LAST 30 DAYS
                </div>
                <div className="text-xs text-neutral-500">{analytics.total_views_30d} total views</div>
              </div>
              {analytics.top_pages.length === 0 ? (
                <p className="mt-6 text-sm text-neutral-500">
                  No page views tracked yet — visit some pages on the site to populate this list.
                </p>
              ) : (
                <div className="mt-4 space-y-2">
                  {(() => {
                    const max = Math.max(1, ...analytics.top_pages.map((p) => p.views));
                    return analytics.top_pages.map((p) => {
                      const pct = Math.round((p.views / max) * 100);
                      return (
                        <div key={p.path} className="flex items-center gap-3">
                          <div className="w-64 truncate text-sm text-neutral-800 font-semibold" title={p.title}>
                            {p.title || p.path}
                          </div>
                          <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                            <div className="h-full bg-teal-500" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="w-14 text-right text-sm text-neutral-700 tabular-nums">{p.views}</div>
                          <div className="w-40 truncate text-xs text-neutral-400 font-mono">{p.path}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-2 flex-wrap">
          {["all", "new", "read", "replied"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${
                filter === f
                  ? "bg-teal-500 border-teal-500 text-white"
                  : "bg-white border-neutral-200 text-neutral-700 hover:border-teal-400"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100 text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Message</th>
                  <th className="text-left px-4 py-3">Received</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((i) => (
                  <tr key={i.id} className="border-t border-neutral-100 align-top">
                    <td className="px-4 py-3 font-semibold text-neutral-900">{i.name}</td>
                    <td className="px-4 py-3 text-neutral-700">
                      <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-neutral-400" /> {i.mobile}</div>
                      {i.email && <div className="flex items-center gap-2 mt-1"><Mail className="w-3 h-3 text-neutral-400" /> {i.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-neutral-700 max-w-md">
                      <p className="line-clamp-3">{i.message}</p>
                    </td>
                    <td className="px-4 py-3 text-neutral-500 whitespace-nowrap">
                      {new Date(i.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={i.status}
                        onChange={(e) => changeStatus(i.id, e.target.value)}
                        className={badge(i.status) + " cursor-pointer"}
                      >
                        <option value="new">new</option>
                        <option value="read">read</option>
                        <option value="replied">replied</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(i.id)}
                        className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 text-xs font-semibold"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {inquiries.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-neutral-500">
                      No inquiries yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Admin;
